<script setup lang="ts">
import {
  ref,
  computed,
  reactive,
  watch,
  onMounted,
  getCurrentInstance,
  defineComponent,
  shallowRef,
  useSlots,
  type Component,
} from 'vue';
import { required as requiredRule } from '@regle/rules';
import { useItpFormControl, useItpFormField } from './useItpForm';
import {
  useItpSelectProvider,
  normalizeOptionsInput,
} from './useItpSelectProvider';
import vSelect from 'vue-select';
import { createPopper } from '@popperjs/core';
import 'vue-select/dist/vue-select.css';

type Mode = 'native' | 'rich';

const props = defineProps<{
  modelValue?: any;
  name: string;
  mode?: Mode;

  multiple?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  nullOption?: boolean;

  valueField?: string;
  textField?: string;
  disabledField?: string;

  options?: any[] | Record<string, any> | ((q: string) => Promise<any[]>);
  initialOptions?: any[];
  dataSource?:
    | { name: string; parameters?: Record<string, any> }
    | (() => { name: string; parameters?: Record<string, any> });
  prefetch?: boolean;

  clearable?: boolean;
  bindObject?: boolean;

  optionTemplate?: string;
  useOptionTemplateForSelectedOption?: boolean;

  request?: (name: string, parameters: Record<string, any>) => Promise<any>;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: any): void;
  (event: 'change', value: any): void;
  (event: 'input', value: any): void;
  (event: 'optionSelected', value: any): void;
}>();

const { isDisabled, isReadonly } = useItpFormControl(props);

function toValidationValue(val: any): any {
  if (val === null || val === undefined) {
    return val;
  }
  if (Array.isArray(val)) {
    return val.length > 0 ? val.map(toValidationValue) : val;
  }
  if (typeof val === 'object') {
    const vf = props.valueField ?? 'value';
    return val[vf] ?? '__has_value__';
  }
  return val;
}

const formFieldProps = reactive({
  name: computed(() => props.name),
  modelValue: computed(() => toValidationValue(props.modelValue)),
  required: computed(() => props.required),
});

const { touch } = useItpFormField(formFieldProps, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

const instance = getCurrentInstance();
const $app = (instance?.proxy as any)?.$app ?? null;
const connections = $app?.connections;
const isRich = computed(() => props.mode !== 'native');

const slots = useSlots();
const RESERVED_SELECT_SLOTS = ['option', 'selected-option', 'no-options'];
const passthroughSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !RESERVED_SELECT_SLOTS.includes(name)),
);

const effectiveValueField = computed(() => props.valueField ?? 'value');
const effectiveTextField = computed(() => props.textField ?? 'text');

const knownOptions = ref<Map<any, any>>(new Map());

const provider = useItpSelectProvider({
  options: props.options as any,
  initialOptions: props.initialOptions,
  dataSource: (() => props.dataSource) as any,
  prefetch: props.prefetch,
  valueField: props.valueField,
  textField: props.textField,
  disabledField: props.disabledField,
  request:
    props.request ??
    (connections
      ? (name: string, parameters: Record<string, any>) =>
          connections.request(name, { parameters })
      : undefined),
});

function resolveField(obj: any, path: string): any {
  if (obj == null || typeof obj !== 'object') {
    return undefined;
  }
  if (!path.includes('.')) {
    return obj[path];
  }
  return path.split('.').reduce((acc: any, key: string) => acc?.[key], obj);
}

function getObjectLabel(option: any): string {
  const textFields = effectiveTextField.value;
  const fields = textFields
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);
  for (const field of fields) {
    const val = resolveField(option, field);
    if (val != null && String(val) !== '') {
      return String(val);
    }
  }

  const fallbacks = [
    'name',
    'label',
    'text',
    'title',
    effectiveValueField.value,
  ];
  for (const field of fallbacks) {
    if (fields.includes(field)) {
      continue;
    }
    const val = option[field];
    if (val != null && String(val) !== '') {
      return String(val);
    }
  }
  return '';
}

function getOptionLabel(option: any): string {
  if (option === null || option === undefined) {
    return '';
  }
  if (typeof option !== 'object') {
    const known = knownOptionFor(option);
    if (known) {
      return getObjectLabel(known);
    }
    if (provider.isAsync() && props.valueField) {
      return '';
    }
    return String(option);
  }
  return getObjectLabel(option);
}

function getOptionKey(option: any): any {
  if (option === null || option === undefined) {
    return String(option);
  }
  if (typeof option !== 'object') {
    return option;
  }
  return option[effectiveValueField.value] ?? JSON.stringify(option);
}

function isOptionSelected(option: any): boolean {
  if (
    !props.multiple ||
    !Array.isArray(props.modelValue) ||
    props.modelValue.length === 0
  ) {
    return false;
  }
  const optionKey = getOptionKey(option);
  if (optionKey === null || optionKey === undefined) {
    return false;
  }
  return props.modelValue.some(
    (selected: any) => getOptionKey(selected) === optionKey,
  );
}

function isOptionSelectable(option: any): boolean {
  return !isOptionSelected(option);
}

function withPopper(
  dropdownList: HTMLElement,
  component: any,
  { width }: { width: string },
) {
  dropdownList.style.width = width;
  const referenceElement = component.$refs.toggle || component.$el;

  const popper = createPopper(referenceElement, dropdownList, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, -1] },
      },
    ],
  });

  return () => popper.destroy();
}

const nativeOptions = computed(() => {
  const initialOpts = props.initialOptions;
  const normalized = normalizeOptionsInput(props.options);
  const list =
    initialOpts && initialOpts.length > 0
      ? initialOpts
      : Array.isArray(normalized)
        ? normalized
        : [];
  return list.map((item) => {
    const n = provider.toNormalized(item);
    return {
      value: n.value,
      text: n.label,
      disabled: n.disabled || false,
      raw: n.raw,
    };
  });
});

function getItemKey(item: any): any {
  if (item === null || item === undefined) {
    return item;
  }
  if (typeof item !== 'object') {
    return item;
  }
  return item[effectiveValueField.value];
}

const nativeValue = computed({
  get() {
    if (props.bindObject) {
      if (props.multiple) {
        if (Array.isArray(props.modelValue)) {
          return props.modelValue.map((obj) => getItemKey(obj));
        }
        return [];
      }
      return getItemKey(props.modelValue) ?? null;
    }
    return props.modelValue ?? (props.multiple ? [] : null);
  },
  set(newVal) {
    let output: any = newVal;
    let rawOption: any = null;
    if (props.bindObject) {
      if (props.multiple) {
        const valueSet = new Set(Array.isArray(newVal) ? newVal : []);
        const matched = nativeOptions.value.filter((opt) =>
          valueSet.has(opt.value),
        );
        output = matched.map((opt) => opt.raw);
        rawOption = output;
      } else {
        const found = nativeOptions.value.find((opt) => opt.value === newVal);
        output = found?.raw ?? null;
        rawOption = found?.raw ?? null;
      }
    } else {
      const found = nativeOptions.value.find((opt) => opt.value === newVal);
      rawOption = found?.raw ?? null;
    }
    emit('update:modelValue', output);
    emit('change', output);
    emit('input', output);
    if (output != null) {
      emit('optionSelected', { value: output, option: rawOption });
    }
  },
});

function reduceOption(option: any): any {
  if (option === null || option === undefined) {
    return option;
  }
  if (typeof option !== 'object') {
    return option;
  }
  if (props.bindObject) {
    return option;
  }
  return option[effectiveValueField.value];
}

function rememberOptions(options: any[]) {
  for (const option of options) {
    if (option && typeof option === 'object') {
      knownOptions.value.set(reduceOption(option), option);
    }
  }
}

function knownOptionFor(value: any): any {
  const key = value && typeof value === 'object' ? reduceOption(value) : value;
  return key == null ? undefined : knownOptions.value.get(key);
}

function selectedOptionObject(value: any): any {
  if (value && typeof value === 'object') {
    return value;
  }
  return knownOptionFor(value) ?? null;
}

const richOptions = ref<any[]>(
  isRich.value
    ? provider.loadInitial().map((inputItem) => inputItem.raw ?? inputItem)
    : [],
);
rememberOptions(richOptions.value);

const richLoading = ref(false);
let searchSeq = 0;

const richSearchText = ref('');

const richSizeClass = computed(() => ({
  'size-sm': props.size === 'sm',
  'size-lg': props.size === 'lg',
}));

function isEmptyValue(val: any): boolean {
  if (val === null || val === undefined) {
    return true;
  }
  if (
    typeof val === 'object' &&
    !Array.isArray(val) &&
    Object.keys(val).length === 0
  ) {
    return true;
  }
  return false;
}

const selectedOptions = computed<any[]>(() => {
  const value = props.modelValue;
  if (isEmptyValue(value)) {
    return [];
  }
  return (Array.isArray(value) ? value : [value])
    .map((inputValue) =>
      inputValue && typeof inputValue === 'object'
        ? inputValue
        : knownOptionFor(inputValue),
    )
    .filter(Boolean);
});

const vselectOptions = computed<any[]>(() => {
  const searching = richSearchText.value.trim().length > 0;
  if (!selectedOptions.value.length || (searching && !props.multiple)) {
    return richOptions.value;
  }
  const present = new Set(richOptions.value.map((opt) => reduceOption(opt)));
  const missing = selectedOptions.value.filter(
    (opt) => !present.has(reduceOption(opt)),
  );
  return missing.length
    ? [...missing, ...richOptions.value]
    : richOptions.value;
});

const richSelected = computed({
  get() {
    const val = props.modelValue;
    if (isEmptyValue(val)) {
      return props.multiple ? [] : null;
    }
    return val;
  },
  set(newVal) {
    let rawOption: any = newVal;
    if (newVal != null && typeof newVal !== 'object') {
      rawOption =
        richOptions.value.find((opt) => reduceOption(opt) === newVal) ?? newVal;
    }

    rememberOptions(
      (Array.isArray(newVal) ? newVal : [rawOption]).map((inputValue) =>
        inputValue != null && typeof inputValue === 'object'
          ? inputValue
          : richOptions.value.find((opt) => reduceOption(opt) === inputValue),
      ),
    );

    emit('update:modelValue', newVal);
    emit('change', newVal);
    emit('input', newVal);
    if (newVal != null) {
      emit('optionSelected', { value: newVal, option: rawOption });
    }
  },
});

async function fetchRichOptions(
  query: string,
  loading?: (isLoading: boolean) => void,
) {
  const seq = ++searchSeq;
  loading?.(true);
  richLoading.value = true;

  try {
    const normalized = await provider.search(query);
    if (seq !== searchSeq) {
      return;
    }
    richOptions.value = normalized.map((item) => item.raw ?? item);
    rememberOptions(richOptions.value);
  } catch (error) {
    console.error('[ItpFormSelect] Search error:', error);
  } finally {
    if (seq === searchSeq) {
      loading?.(false);
      richLoading.value = false;
    }
  }
}

function onRichSearch(query: string, loading: (b: boolean) => void) {
  if (!provider.isAsync()) {
    return;
  }
  richSearchText.value = query ?? '';
  const trimmed = richSearchText.value.trim();
  void fetchRichOptions(trimmed || '*', loading);
}

function onRichOpen() {
  if (!provider.isAsync() || richSearchText.value.trim()) {
    return;
  }
  void fetchRichOptions('*');
}

function onRichClose() {
  richSearchText.value = '';
  touch();
}

onMounted(() => {
  if (isRich.value) {
    if (props.prefetch && provider.isAsync()) {
      const seq = ++searchSeq;
      richLoading.value = true;
      provider
        .search('')
        .then((normalized) => {
          if (seq === searchSeq) {
            richOptions.value = normalized.map((n) => n.raw ?? n);
            rememberOptions(richOptions.value);
          }
        })
        .catch((error) =>
          console.error('[ItpFormSelect] Prefetch error:', error),
        )
        .finally(() => {
          if (seq === searchSeq) {
            richLoading.value = false;
          }
        });
    }
  }
});

watch(
  () => props.options,
  (newOptions) => {
    if (isRich.value) {
      const normalized = normalizeOptionsInput(newOptions);
      if (Array.isArray(normalized)) {
        richOptions.value = normalized;
      }
    }
  },
  { immediate: true },
);

const compiledOptionComponent = shallowRef<Component | null>(null);

watch(
  () => props.optionTemplate,
  (tmpl) => {
    if (tmpl) {
      compiledOptionComponent.value = defineComponent({
        name: 'CompiledOptionTemplate',
        props: ['option'],
        template: tmpl,
      });
    } else {
      compiledOptionComponent.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <select
    v-if="!isRich"
    :id="name"
    v-model="nativeValue"
    :name="name"
    :multiple="multiple"
    :disabled="isDisabled || isReadonly"
    class="form-select"
    :class="{
      'form-select-sm': size === 'sm',
      'form-select-lg': size === 'lg',
    }"
    @change="touch"
  >
    <option v-if="nullOption && !multiple" :value="null" />
    <option v-else-if="!multiple && nativeValue == null" :value="null" disabled>
      {{ placeholder || '---' }}
    </option>
    <option
      v-for="opt in nativeOptions"
      :key="opt.value ?? String(opt.text)"
      :value="opt.value"
      :disabled="opt.disabled"
    >
      {{ opt.text }}
    </option>
  </select>

  <vSelect
    v-else
    :id="name"
    v-model="richSelected"
    :class="richSizeClass"
    :name="name"
    :options="vselectOptions"
    :multiple="multiple"
    :clearable="clearable"
    :searchable="true"
    :placeholder="placeholder"
    :loading="richLoading"
    :label="effectiveTextField"
    :reduce="reduceOption"
    :get-option-label="getOptionLabel"
    :get-option-key="getOptionKey"
    :selectable="isOptionSelectable"
    :dropdown-should-open="(vs: any) => !vs.noDrop && vs.open"
    :disabled="isDisabled || isReadonly"
    :append-to-body="true"
    :calculate-position="withPopper"
    :filterable="!provider.isAsync()"
    @search="onRichSearch"
    @open="onRichOpen"
    @close="onRichClose"
  >
    <template #option="slotProps">
      <slot name="option" v-bind="slotProps">
        <component
          :is="compiledOptionComponent"
          v-if="compiledOptionComponent"
          :option="slotProps"
        />
        <template v-else>{{ getOptionLabel(slotProps) }}</template>
      </slot>
    </template>

    <template #selected-option="slotProps">
      <slot name="selected-option" v-bind="slotProps">
        <component
          :is="compiledOptionComponent"
          v-if="
            compiledOptionComponent &&
            useOptionTemplateForSelectedOption &&
            selectedOptionObject(slotProps)
          "
          :option="selectedOptionObject(slotProps)"
        />
        <template v-else>{{ getOptionLabel(slotProps) }}</template>
      </slot>
    </template>

    <template #no-options>
      <slot name="no-options">No results</slot>
    </template>

    <template v-for="name in passthroughSlotNames" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
  </vSelect>
</template>

<style lang="scss">
@import 'vue-select/dist/vue-select.css';

:root {
  --vs-dropdown-z-index: 1200;
}

.v-select {
  --vs-controls-size: 0.75;
  --vs-actions-padding: 0 8px 0 3px;
  --vs-border-radius: var(--bs-border-radius);
  --vs-search-input-placeholder-color: #6c757d;

  display: block;
  width: 100%;

  .vs__dropdown-toggle {
    min-height: calc(1.5em + 0.75rem + calc(var(--bs-border-width) * 2));
    padding: 0;
    width: 100%;
    align-items: center;
  }

  &.size-sm {
    .vs__dropdown-toggle {
      min-height: calc(1.5em + 0.5rem + calc(var(--bs-border-width) * 2));
      font-size: 0.875rem;
    }

    .vs__search,
    .vs__search:focus,
    .vs__selected {
      font-size: 0.875rem;
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .vs__selected-options {
    align-items: center;
    padding: 0 4px;
  }

  .vs__search,
  .vs__search:focus {
    margin: 0;
    padding: 0 4px;
    font-size: inherit;
  }

  .vs__selected {
    margin: 2px 4px 2px 0;
    padding: 1px 6px;
    font-size: inherit;
    line-height: 1.4;
  }

  .vs__deselect {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    line-height: 1;

    svg {
      display: block;
    }
  }

  .vs__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 8px 0 3px;
  }

  .vs__clear {
    transform: none;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: 1rem;
    padding: 0;
  }

  .vs__open-indicator {
    display: none;
  }

  .vs__actions::after {
    content: '';
    display: block;
    width: 16px;
    height: 12px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 12px;
    pointer-events: none;
    flex-shrink: 0;
    transition: transform 0.15s ease-in-out;
  }

  &.vs--open .vs__actions::after {
    transform: rotate(180deg);
  }
}

.input-group > .v-select {
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
}

.input-group > .v-select:not(:first-child) .vs__dropdown-toggle {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.input-group > .v-select:not(:last-child) .vs__dropdown-toggle {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
